import API from '@Services/apiAxios';
import axios from 'axios';
import { formatShipmentNumber } from '../types';

export interface PresignedUrlResponse {
  url: string;
  fields: Record<string, string>;
  key: string;
}

export interface VendorInvoiceCharge {
  name: string;
  amount: number;
}

export interface VendorInvoiceShipment {
  shipmentNumber?: string | null;
  awbNumber?: string | null;
  actualWeight?: number | null;
  volumetricWeight?: number | null;
  chargeableWeight?: number | null;
  unit?: string | null;
  length?: number | null;
  breadth?: number | null;
  height?: number | null;
  weight?: number | null;
  numberOfBoxes?: number | null;
  isTaxed?: boolean | string | null;
  carrierReferenceNumber?: string | null;
  charges?: VendorInvoiceCharge[];
  [key: string]: any;
}

export interface VendorInvoiceGroup {
  carrierReferenceNumber?: string | null;
  shipments?: VendorInvoiceShipment[];
  [key: string]: any;
}

export interface VendorInvoiceExtractionResponse {
  jobId?: string;
  status?: string;
  documentType?: string;
  confidence?: number;
  invalidFields?: string[];
  error?: string | null;
  fields?: {
    invoices?: VendorInvoiceGroup[];
    shipments?: VendorInvoiceShipment[];
    [key: string]: any;
  };
  [key: string]: any;
}

/**
 * Step 1: Request presigned upload URL for vendor invoice extraction
 * POST /speedboxbot/extract/upload-url
 */
export const getUploadUrl = async (file: File): Promise<PresignedUrlResponse> => {
  const { data } = await API.post<PresignedUrlResponse>('/speedboxbot/extract/upload-url', {
    fileName: file.name,
    mimeType: file.type || 'application/octet-stream',
  });
  return data;
};

/**
 * Step 2: Upload file directly to S3 bucket via presigned POST
 */
export const uploadToS3 = async (
  uploadUrlResponse: PresignedUrlResponse,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<void> => {
  const { url, fields } = uploadUrlResponse;
  const formData = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  formData.append('file', file);

  await axios.post(url, formData, {
    onUploadProgress: progressEvent => {
      if (onProgress) {
        const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
        onProgress(percent);
      }
    },
  });
};

/**
 * Step 3: Trigger vendor invoice document extraction with documentType = "vendor_invoice"
 * POST /speedboxbot/extract
 */
export const extractVendorInvoiceDocument = async (s3Key: string): Promise<VendorInvoiceExtractionResponse> => {
  const { data } = await API.post<VendorInvoiceExtractionResponse>('/speedboxbot/extract', {
    documentType: 'vendor_invoice',
    s3Key,
  });
  return data;
};

/**
 * Full 3-step AI extraction runner
 */
export const processVendorInvoiceExtraction = async (
  file: File,
  onStatusChange?: (status: string) => void,
  onUploadProgress?: (percent: number) => void,
): Promise<VendorInvoiceExtractionResponse> => {
  onStatusChange?.('Generating secure upload URL...');
  const presigned = await getUploadUrl(file);

  onStatusChange?.('Uploading invoice document to S3...');
  const s3Key = presigned.key || presigned.fields?.['key'];
  await uploadToS3(presigned, file, onUploadProgress);

  onStatusChange?.('Extracting vendor invoice details using AI...');
  const response = await extractVendorInvoiceDocument(s3Key);

  return response;
};

export const toNumber = (value: any): number => {
  if (value === null || value === undefined || value === '') return 0;

  if (typeof value === 'string') {
    const cleaned = value.replace(/[$,%]/g, '').replace(/,/g, '').trim();
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : num;
  }

  const num = Number(value);
  return isNaN(num) ? 0 : num;
};

/**
 * Map AI extraction response fields into flat ShipmentInvoice array
 */
export const mapAiInvoicesToShipments = (fields: any): any[] => {
  const allShipments: any[] = [];
  if (!fields) return allShipments;

  const rawInvoices = fields.invoices || fields.shipments || [];

  if (!Array.isArray(rawInvoices)) return allShipments;

  for (const item of rawInvoices) {
    // Case 1: Grouped format ({ carrierReferenceNumber, shipments: [...] })
    if (Array.isArray(item.shipments)) {
      const carrierRef = (item.carrierReferenceNumber || '').toString().trim();

      for (const s of item.shipments) {
        const isTaxedValue =
          typeof s.isTaxed === 'boolean'
            ? s.isTaxed
            : typeof s.isTaxed === 'string'
            ? s.isTaxed.trim().toLowerCase() === 'yes' || s.isTaxed.trim().toLowerCase() === 'true'
            : false;

        const charges = Array.isArray(s.charges)
          ? s.charges
              .map((c: any) => ({
                name: String(c.name || '').trim(),
                amount: toNumber(c.amount),
              }))
              .filter((c: any) => c.name && c.amount !== 0)
          : [];

        const shipmentObj = {
          shipmentNumber: formatShipmentNumber(s.shipmentNumber) || '',
          awbNumber: s.awbNumber ? String(s.awbNumber).trim() : '',
          unit: s.unit ? String(s.unit).trim() : 'kg-cm',
          isTaxed: isTaxedValue,
          actualWeight: toNumber(s.actualWeight),
          chargeableWeight: toNumber(s.chargeableWeight),
          length: toNumber(s.length ?? s.volumetricWeight),
          breadth: toNumber(s.breadth),
          height: toNumber(s.height),
          weight: toNumber(s.weight),
          numberOfBoxes: toNumber(s.numberOfBoxes) || 1,
          carrierReferenceNumber:
            carrierRef || (s.carrierReferenceNumber ? String(s.carrierReferenceNumber).trim() : ''),
          charges,
        };

        allShipments.push(shipmentObj);
      }
    } else {
      // Case 2: Flat shipment format ({ shipmentNumber, awbNumber, carrierReferenceNumber, ... })
      const isTaxedValue =
        typeof item.isTaxed === 'boolean'
          ? item.isTaxed
          : typeof item.isTaxed === 'string'
          ? item.isTaxed.trim().toLowerCase() === 'yes' || item.isTaxed.trim().toLowerCase() === 'true'
          : false;

      const charges = Array.isArray(item.charges)
        ? item.charges
            .map((c: any) => ({
              name: String(c.name || '').trim(),
              amount: toNumber(c.amount),
            }))
            .filter((c: any) => c.name && c.amount !== 0)
        : [];

      const shipmentObj = {
        shipmentNumber: formatShipmentNumber(item.shipmentNumber) || '',
        awbNumber: item.awbNumber ? String(item.awbNumber).trim() : '',
        unit: item.unit ? String(item.unit).trim() : 'kg-cm',
        isTaxed: isTaxedValue,
        actualWeight: toNumber(item.actualWeight),
        chargeableWeight: toNumber(item.chargeableWeight),
        length: toNumber(item.length ?? item.volumetricWeight),
        breadth: toNumber(item.breadth),
        height: toNumber(item.height),
        weight: toNumber(item.weight),
        numberOfBoxes: toNumber(item.numberOfBoxes) || 1,
        carrierReferenceNumber: item.carrierReferenceNumber ? String(item.carrierReferenceNumber).trim() : '',
        charges,
      };

      allShipments.push(shipmentObj);
    }
  }

  return allShipments;
};
